import { RefObject, useCallback, useEffect, useState } from 'react';

type TouchHandlers = {
  onLeftClick: () => void;
  onRightClick: () => void;
};

type Direction = 'left' | 'right';

const ELEMENT_MARGIN = 8;

export const useSwitching = (delay: number): [boolean, () => void] => {
  const [isSwitching, setIsSwitching] = useState<boolean>(false);

  const handleSwitch = useCallback(() => {
    setIsSwitching(true);
    setTimeout(() => {
      setIsSwitching(false);
    }, delay);
  }, [delay]);

  return [isSwitching, handleSwitch];
};

export const useTouchHandlers = (
  ref: RefObject<HTMLElement>,
  { onLeftClick, onRightClick }: TouchHandlers,
) => {
  useEffect(() => {
    const root = ref.current;
    let touchStartX: number;

    const touchStartHandler = (event: TouchEvent) => {
      const touchObj = event.changedTouches[0];
      touchStartX = touchObj.pageX;
    };

    const touchMoveHandler = (event: TouchEvent) => {
      event.preventDefault();
    };

    const touchEndHandler = (event: TouchEvent) => {
      const touchObj = event.changedTouches[0];
      if (touchStartX < touchObj.pageX) {
        onLeftClick();
      } else if (touchStartX > touchObj.pageX) {
        onRightClick();
      }
    };

    root?.addEventListener('touchstart', touchStartHandler);
    root?.addEventListener('touchmove', touchMoveHandler);
    root?.addEventListener('touchend', touchEndHandler);

    return () => {
      root?.removeEventListener('touchstart', touchStartHandler);
      root?.removeEventListener('touchmove', touchMoveHandler);
      root?.removeEventListener('touchend', touchEndHandler);
    };
  }, [onLeftClick, onRightClick]);
};

export const useMoveWidthCalculator = (
  cloudBoxItemsRef: RefObject<NodeListOf<Element>>,
  cloudBoxRef: RefObject<HTMLDivElement>,
  scrollOffset: number,
) => {
  const [activeItemIndex, setActiveItemIndex] = useState(0);

  return useCallback(
    (direction: Direction) => {
      let nextMoveWidth = 0;

      if (direction === 'left') {
        for (let i = activeItemIndex; i >= 0; i--) {
          const currentItem = cloudBoxItemsRef.current?.[i];
          const { left: itemLeft } = currentItem?.getBoundingClientRect() || {};
          const { left: containerLeft } = cloudBoxRef.current?.getBoundingClientRect() || {};
          const { width: itemWidth } = currentItem?.getBoundingClientRect() || {};

          if (itemLeft < containerLeft) {
            nextMoveWidth = containerLeft - itemLeft + ELEMENT_MARGIN - scrollOffset / 2;
            if ((100 * (containerLeft - itemLeft)) / itemWidth < 10) {
              nextMoveWidth =
                nextMoveWidth +
                (cloudBoxItemsRef.current?.[i - 1]?.getBoundingClientRect().width || 0) +
                ELEMENT_MARGIN;
              setActiveItemIndex(i - 2);
            }
            setActiveItemIndex(i - 1);
            break;
          } else if (i - 1 >= 0) {
            nextMoveWidth = cloudBoxItemsRef.current?.[i - 1]?.clientWidth || 0;
            setActiveItemIndex(i - 1);
          }
        }
      } else {
        for (let i = activeItemIndex; i < (cloudBoxItemsRef.current?.length || 0); i++) {
          const currentItem = cloudBoxItemsRef.current?.[i];
          const { right: itemRight } = currentItem?.getBoundingClientRect() || {};
          const { right: containerRight } = cloudBoxRef.current?.getBoundingClientRect() || {};

          if (itemRight > containerRight) {
            nextMoveWidth = itemRight - containerRight + ELEMENT_MARGIN + scrollOffset;
            if ((100 * nextMoveWidth) / itemRight < 10) {
              nextMoveWidth =
                nextMoveWidth +
                (cloudBoxItemsRef.current?.[i + 1]?.getBoundingClientRect().width || 0) +
                ELEMENT_MARGIN;
              setActiveItemIndex(i + 2);
            }
            setActiveItemIndex(i + 1);
            break;
          } else if (i + 1 < (cloudBoxItemsRef.current?.length || 0)) {
            nextMoveWidth = cloudBoxItemsRef.current?.[i + 1]?.clientWidth || 0;
            setActiveItemIndex(i + 1);
          }
        }
      }

      return nextMoveWidth;
    },
    [activeItemIndex, cloudBoxItemsRef, cloudBoxRef, scrollOffset],
  );
};
